//src/components/MarketIndicators.js 

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { Card, Row, Col } from 'react-bootstrap';
// 👈 NOUVEL IMPORT : Composant de lien de React Router
import { Link } from 'react-router-dom';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip
);

const MarketIndicators = () => {
    const [indicators, setIndicators] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchIndicators = async () => {
            try {
                const res = await axios.get('/api/market/indicators');
                if (Object.keys(res.data).length > 0) {
                    setIndicators(res.data);
                } else {
                    setError('Aucune donnée d\'indicateur n\'a été reçue.');
                }
            } catch (err) {
                console.error(err);
                setError('Impossible de charger les indicateurs du marché.');
            } finally {
                setLoading(false);
            }
        };

        fetchIndicators();
    }, []);

    if (loading) {
        return <p className="text-center">Chargement des indicateurs...</p>;
    }

    if (error) {
        return <div className="text-danger text-center p-3">{error}</div>; 
    }

    if (!indicators) {
        return <div className="p-3">Aucune donnée à afficher.</div>;
    }

    // Options du graphique (mini-graphique)
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
        },
        scales: {
            x: { display: false },
            y: { display: false },
        },
        elements: {
            point: { radius: 0 },
            line: { tension: 0.4 },
        }
    };

    const getChartData = (history, color) => ({
        labels: history.map(item => new Date(item.date).toLocaleDateString()),
        datasets: [{
            data: history.map(item => item.value),
            borderColor: color,
            borderWidth: 1.5,
            fill: false,
        }]
    });

    const indicatorOrder = ['vix', 'cpi', 'gdp', 'tenY'];
    const indicatorColors = {
        vix: '#4e73df', // Bleu
        cpi: '#1cc88a', // Vert
        gdp: '#f6c23e', // Jaune
        tenY: '#e74a3b' // Rouge
    };

    /**
     * Calcule la variation en pourcentage et détermine la couleur de la tendance.
     * @param {Array} history - L'historique des valeurs.
     * @returns {{ trendClass: string, change: string }}
     */
    const getTrendInfo = (history) => {
        if (!history || history.length < 2) {
            return { trendClass: 'text-secondary', change: 'N/A' };
        }

        const latestValue = parseFloat(history[history.length - 1].value);
        const previousValue = parseFloat(history[history.length - 2].value);

        if (isNaN(latestValue) || isNaN(previousValue) || previousValue === 0) {
            return { trendClass: 'text-secondary', change: 'N/A' };
        }

        const change = latestValue - previousValue;
        const percentChange = (change / previousValue) * 100;
        
        let trendClass = 'text-secondary';
        if (change > 0) {
            trendClass = 'text-success'; // Vert
        } else if (change < 0) {
            trendClass = 'text-danger'; // Rouge
        }

        const changeSign = change > 0 ? '+' : '';
        const changeText = `${changeSign}${percentChange.toFixed(2)}%`;

        return { trendClass, change: changeText };
    };


    return (
        <div className="mt-4">
            <Row className="g-4"> 
                {indicatorOrder.map(key => {
                    const indicator = indicators[key];

                    if (!indicator || !indicator.history || indicator.history.length === 0) {
                        return null; 
                    }

                    const currentValue = indicator.currentValue ? parseFloat(indicator.currentValue).toFixed(2) : 'N/A';
                    const { trendClass, change } = getTrendInfo(indicator.history);
                    const chartColor = indicatorColors[key] || '#4e73df';

                    return (
                        <Col key={key} md={6} lg={3} className="mb-4">
                            {/* 👈 LE CHANGEMENT CLÉ : Envelopper la carte dans un Link */}
                            <Link 
                                to={`/indicators/${key}`} // Navigue vers /indicators/vix, /indicators/cpi, etc.
                                style={{ textDecoration: 'none', color: 'inherit' }} // Pour que le lien n'ait pas de style par défaut
                            >
                                <Card className="shadow h-100 py-2 border-left-primary clickable-card"> 
                                    <Card.Body>
                                        <Row className="no-gutters align-items-center">
                                            <Col className="mr-2">
                                                <div className="text-xs fw-bold text-uppercase mb-1" style={{ color: chartColor }}>
                                                    {indicator.name}
                                                </div>
                                                <div className="h5 mb-0 fw-bold text-gray-800">
                                                    {currentValue}
                                                </div>
                                                {/* Affichage de la variation avec la couleur de tendance */}
                                                <div className={`mt-1 fw-bold ${trendClass}`} style={{ fontSize: '0.9rem' }}>
                                                    {change}
                                                </div>
                                            </Col>
                                            <Col xs="auto">
                                                {/* Mini-graphique à droite */}
                                                <div style={{ width: '80px', height: '40px' }}>
                                                    <Line
                                                        data={getChartData(indicator.history.slice(-10), chartColor)} // Les 10 dernières données
                                                        options={chartOptions}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Link>
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
};

export default MarketIndicators;